import { CloseCircleFilled } from "@ant-design/icons";
import { Empty, Input, Select } from "antd";
import useUserDeletionRequests from "hooks/userdeletion/useUserDeletionRequests";
import useDebounce from "hooks/route-rules/useDebounce";
import React, { useCallback, useEffect, useState } from "react";
import { Spinner, Col, Row } from "react-bootstrap";
import { toast } from "react-toastify";
import WidgetSectionTitle from "widgets/WidgetSectionTitle";
import UserDeletionActionModal from "./UserDeletionActionModal";
import UserDeletionRequestsTable from "./UserDeletionRequestsTable";
import UserDeletionViewModal from "./UserDeletionViewModal";

const STATUS_OPTIONS = [
  { label: "All Statuses", value: "ALL" },
  { label: "Requested", value: "REQUESTED" },
  { label: "Approved", value: "APPROVED" },
  { label: "Scheduled", value: "SCHEDULED" },
  { label: "Failed", value: "FAILED" },
  { label: "Deleted", value: "DELETED" },
];

export default function UserDeletionRequests() {
  const {
    loading,
    getAllUserDeletionRequests,
    getUserDeletionRequestById,
    updateUserDeletionRequestStatus,
    scheduleUserDeletion,
    markUserDeletionFailed,
    markUserDeletionDeleted,
    deleteUserDeletionRequest,
  } = useUserDeletionRequests();

  const [requests, setRequests] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState("ALL");
  const [userIdFilter, setUserIdFilter] = useState("");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [totalElements, setTotalElements] = useState(0);
  const [isFetchingPage, setIsFetchingPage] = useState(false);

  const [showViewModal, setShowViewModal] = useState(false);
  const [isLoadingView, setIsLoadingView] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);

  const [showActionModal, setShowActionModal] = useState(false);
  const [actionType, setActionType] = useState(null);
  const [actionTarget, setActionTarget] = useState(null);
  const [isSubmittingAction, setIsSubmittingAction] = useState(false);

  const debouncedUserIdFilter = useDebounce(userIdFilter, 300);

  const loadUserDeletionRequests = useCallback(async () => {
    setIsFetchingPage(true);
    try {
      const response = await getAllUserDeletionRequests({
        userId: debouncedUserIdFilter?.trim() || undefined,
        status: selectedStatus !== "ALL" ? selectedStatus : undefined,
        page,
        size: pageSize,
      });

      const content = Array.isArray(response?.content) ? response.content : [];
      setRequests(content);
      setTotalPages(response?.totalPages ?? 1);
      setTotalElements(response?.totalElements ?? content.length);
    } catch (err) {
      toast.error(err?.message || "Failed to fetch user deletion requests", {
        theme: "colored",
      });
      setRequests([]);
      setTotalPages(1);
      setTotalElements(0);
    } finally {
      setIsFetchingPage(false);
    }
  }, [
    debouncedUserIdFilter,
    getAllUserDeletionRequests,
    page,
    pageSize,
    selectedStatus,
  ]);

  useEffect(() => {
    loadUserDeletionRequests();
  }, [loadUserDeletionRequests]);

  const handleView = async (id) => {
    setShowViewModal(true);
    setIsLoadingView(true);
    try {
      const record = await getUserDeletionRequestById(id);
      setSelectedRequest(record || null);
    } catch (err) {
      toast.error(err?.message || "Failed to load request details", {
        theme: "colored",
      });
      setSelectedRequest(null);
    } finally {
      setIsLoadingView(false);
    }
  };

  const handleApprove = async (id) => {
    try {
      await updateUserDeletionRequestStatus(id, { status: "APPROVED" });
      toast.success("Request approved", { theme: "colored" });
      await loadUserDeletionRequests();
    } catch (err) {
      toast.error(err?.message || "Failed to approve request", {
        theme: "colored",
      });
    }
  };

  const openActionModal = (type, item) => {
    setActionType(type);
    setActionTarget(item);
    setShowActionModal(true);
  };

  const handleActionSubmit = async (payload = {}) => {
    if (!actionTarget?.id || !actionType) return;
    setIsSubmittingAction(true);
    try {
      if (actionType === "schedule") {
        await scheduleUserDeletion(actionTarget.id, payload.deletionScheduledAt);
        toast.success("Deletion scheduled", { theme: "colored" });
      } else if (actionType === "fail") {
        await markUserDeletionFailed(actionTarget.id, payload.failureReason);
        toast.success("Request marked as failed", { theme: "colored" });
      }

      setShowActionModal(false);
      setActionType(null);
      setActionTarget(null);
      await loadUserDeletionRequests();
    } catch (err) {
      toast.error(err?.message || "Failed to update request", {
        theme: "colored",
      });
    } finally {
      setIsSubmittingAction(false);
    }
  };

  const handleMarkDeleted = async (id) => {
    try {
      await markUserDeletionDeleted(id);
      toast.success("Request marked as deleted", { theme: "colored" });
      await loadUserDeletionRequests();
    } catch (err) {
      toast.error(err?.message || "Failed to mark request as deleted", {
        theme: "colored",
      });
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteUserDeletionRequest(id);
      toast.success("Request deleted", { theme: "colored" });
      await loadUserDeletionRequests();
    } catch (err) {
      toast.error(err?.message || "Failed to delete request", {
        theme: "colored",
      });
    }
  };

  return (
    <>
      <WidgetSectionTitle
        icon="user-times"
        title="User Deletion Requests"
        subtitle="View and manage user deletion request lifecycle."
        transform="shrink-2"
        className="mb-3 mt-3"
      />

      <Row
        className="mb-3 align-items-end gx-3 p-2 rounded bg-light mx-auto"
        style={{ maxWidth: 980 }}
      >
        <Col md={8}>
          <div className="text-muted small fw-semibold mb-1">Search By User ID</div>
          <Input
            placeholder='Filter by "userId"...'
            value={userIdFilter}
            onChange={(e) => {
              setUserIdFilter(e.target.value);
              setPage(0);
            }}
            allowClear={{
              clearIcon: (
                <CloseCircleFilled
                  onClick={() => setUserIdFilter("")}
                  style={{ color: "#8c8c8c" }}
                />
              ),
            }}
            style={{ width: "100%", borderRadius: 24 }}
          />
        </Col>
        <Col md={4}>
          <div className="text-muted small fw-semibold mb-1">Status Filter</div>
          <Select
            style={{ width: "100%" }}
            value={selectedStatus}
            onChange={(value) => {
              setSelectedStatus(value);
              setPage(0);
            }}
            options={STATUS_OPTIONS}
          />
        </Col>
      </Row>

      {isFetchingPage && requests.length === 0 ? (
        <div
          style={{
            minHeight: 200,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Spinner animation="border" variant="success" />
        </div>
      ) : requests.length === 0 ? (
        <div
          style={{
            minHeight: 200,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Empty description="No user deletion requests found." />
        </div>
      ) : (
        <UserDeletionRequestsTable
          requests={requests}
          page={page}
          pageSize={pageSize}
          setPage={setPage}
          setPageSize={setPageSize}
          totalPages={totalPages}
          totalElements={totalElements}
          tableLoading={loading || isFetchingPage}
          onView={handleView}
          onApprove={handleApprove}
          onOpenSchedule={(item) => openActionModal("schedule", item)}
          onOpenFail={(item) => openActionModal("fail", item)}
          onMarkDeleted={handleMarkDeleted}
          onDelete={handleDelete}
        />
      )}

      <UserDeletionViewModal
        show={showViewModal}
        onClose={() => {
          setShowViewModal(false);
          setSelectedRequest(null);
        }}
        request={selectedRequest}
        loading={isLoadingView}
      />

      <UserDeletionActionModal
        show={showActionModal}
        onClose={() => {
          setShowActionModal(false);
          setActionType(null);
          setActionTarget(null);
        }}
        actionType={actionType}
        loading={isSubmittingAction}
        onSubmit={handleActionSubmit}
      />
    </>
  );
}
